import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calculator, Check, AlertTriangle, Package, RefreshCw, 
  ChevronDown, ChevronUp, FileCheck, Calendar, TrendingUp,
  Boxes, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SmartComboPlanningTab = () => {
  // State
  const [isCalculating, setIsCalculating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Target month
  const [targetMonth, setTargetMonth] = useState('');
  
  // Plan data
  const [currentPlan, setCurrentPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  
  // Approval dialog
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [modifiedAllocations, setModifiedAllocations] = useState({});
  
  // Expanded rows
  const [expandedCombos, setExpandedCombos] = useState(new Set());

  // Generate month options
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 1; i <= 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const monthOptions = getMonthOptions();

  // Set default target month to next month
  useEffect(() => {
    if (!targetMonth && monthOptions.length > 0) {
      setTargetMonth(monthOptions[0].value);
    }
  }, [monthOptions, targetMonth]);

  // Load existing plans
  const fetchPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const response = await apiClient.get('/combo/planning/plans?limit=20');
      if (response.data.success) {
        setPlans(response.data.plans || []);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Calculate plan
  const handleCalculatePlan = async () => {
    if (!targetMonth) {
      toast.error('Please select a target month');
      return;
    }

    setIsCalculating(true);
    setCurrentPlan(null);
    
    try {
      const response = await apiClient.get(`/combo/planning/calculate?target_month=${targetMonth}`);
      
      if (response.data.success) {
        setCurrentPlan(response.data);
        toast.success(`Plan calculated: ${response.data.summary?.total_possible || 0} combos possible`);
        fetchPlans(); // Refresh plans list
      }
    } catch (error) {
      console.error('Error calculating plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to calculate plan');
    } finally {
      setIsCalculating(false);
    }
  };

  // Open approval dialog
  const openApprovalDialog = () => {
    if (!currentPlan) return;
    
    // Initialize modified allocations with current possible values
    const allocations = {};
    currentPlan.combos?.forEach(combo => {
      allocations[combo.combo_barcode] = combo.possible;
    });
    setModifiedAllocations(allocations);
    setApprovalDialogOpen(true);
  };

  // Handle allocation change
  const handleAllocationChange = (comboBarcode, value) => {
    const numValue = parseInt(value) || 0;
    setModifiedAllocations(prev => ({
      ...prev,
      [comboBarcode]: numValue
    }));
  };

  // Approve plan
  const handleApprovePlan = async () => {
    if (!currentPlan?.plan_id) return;

    setIsApproving(true);
    
    try {
      const allocations = Object.entries(modifiedAllocations).map(([combo_barcode, approved_qty]) => ({
        combo_barcode,
        approved_qty
      }));

      const response = await apiClient.post('/combo/planning/approve', {
        plan_id: currentPlan.plan_id,
        allocations
      });

      if (response.data.success) {
        toast.success(`Plan approved! ${response.data.total_combos_approved} combos, ${response.data.reservations_created} reservations created`);
        setApprovalDialogOpen(false);
        fetchPlans();
        
        // Update current plan status
        setCurrentPlan(prev => prev ? { ...prev, status: 'APPROVED' } : null);
      }
    } catch (error) {
      console.error('Error approving plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve plan');
    } finally {
      setIsApproving(false);
    }
  };

  // Generate pick slips
  const handleGeneratePickSlips = async (planId) => {
    setIsGenerating(true);
    
    try {
      const response = await apiClient.post('/combo/planning/generate-pick-slips', {
        plan_id: planId,
        batch_size: 50
      });

      if (response.data.success) {
        toast.success(`Pick slips generated! ${response.data.sessions_created} sessions, ${response.data.slip_groups_created} slip groups created`);
        fetchPlans();
      }
    } catch (error) {
      console.error('Error generating pick slips:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate pick slips');
    } finally {
      setIsGenerating(false);
    }
  };

  // Toggle expanded combo
  const toggleExpanded = (comboBarcode) => {
    setExpandedCombos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(comboBarcode)) {
        newSet.delete(comboBarcode);
      } else {
        newSet.add(comboBarcode);
      }
      return newSet;
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const styles = {
      'FULL': { bg: 'bg-green-100', text: 'text-green-700', label: 'Full Stock' },
      'PARTIAL': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Partial' },
      'FULFILLED': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Fulfilled' },
      'DRAFT': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Draft' },
      'APPROVED': { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      'PICK_SLIPS_GENERATED': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Pick Slips Ready' },
    };
    const style = styles[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
    return <Badge className={`${style.bg} ${style.text}`}>{style.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header with Month Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-600" />
            Smart Combo Planning
          </CardTitle>
          <CardDescription>
            Calculate optimal combo assembly based on monthly projections and component availability
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-xs">
              <Label>Target Month</Label>
              <Select value={targetMonth} onValueChange={setTargetMonth}>
                <SelectTrigger data-testid="target-month-select">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleCalculatePlan}
              disabled={isCalculating || !targetMonth}
              data-testid="calculate-plan-btn"
            >
              {isCalculating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Calculator className="w-4 h-4 mr-2" />
              )}
              Calculate Plan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current Plan Results */}
      {currentPlan && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Plan: {currentPlan.plan_id}
                </CardTitle>
                <CardDescription>
                  Target: {currentPlan.target_month} • Created: {new Date().toLocaleDateString()}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {currentPlan.plan_id && !['APPROVED', 'PICK_SLIPS_GENERATED'].includes(currentPlan.status) && (
                  <Button onClick={openApprovalDialog} data-testid="approve-plan-btn">
                    <Check className="w-4 h-4 mr-2" />
                    Review & Approve
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-gray-800">{currentPlan.summary?.total_combos || 0}</div>
                <div className="text-xs text-gray-500">Total Combos</div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{currentPlan.summary?.total_projection || 0}</div>
                <div className="text-xs text-gray-500">Projected</div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">{currentPlan.summary?.total_fg_stock || 0}</div>
                <div className="text-xs text-gray-500">FG Stock</div>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-yellow-600">{currentPlan.summary?.total_remaining || 0}</div>
                <div className="text-xs text-gray-500">Remaining</div>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-600">{currentPlan.summary?.total_possible || 0}</div>
                <div className="text-xs text-gray-500">Possible ({currentPlan.summary?.fulfillment_rate || 0}%)</div>
              </div>
            </div>

            {/* Shared Components Alert */}
            {currentPlan.shared_components?.length > 0 && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center gap-2 text-orange-700 font-medium mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  Shared Components Detected
                </div>
                <div className="text-sm text-orange-600">
                  {currentPlan.shared_components.map((comp, idx) => (
                    <span key={comp.barcode}>
                      {comp.name} (shared by: {comp.shared_by?.join(', ')})
                      {idx < currentPlan.shared_components.length - 1 ? ' • ' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Combo Details Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Combo</TableHead>
                    <TableHead className="text-right">Projection</TableHead>
                    <TableHead className="text-right">FG Stock</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead className="text-right">Possible</TableHead>
                    <TableHead>Limiting Factor</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentPlan.combos?.map((combo) => (
                    <React.Fragment key={combo.combo_barcode}>
                      <TableRow 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleExpanded(combo.combo_barcode)}
                      >
                        <TableCell>
                          {expandedCombos.has(combo.combo_barcode) ? 
                            <ChevronUp className="w-4 h-4" /> : 
                            <ChevronDown className="w-4 h-4" />
                          }
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{combo.combo_name}</div>
                          <div className="text-xs text-gray-500">{combo.combo_barcode}</div>
                        </TableCell>
                        <TableCell className="text-right font-medium">{combo.projection}</TableCell>
                        <TableCell className="text-right text-green-600">{combo.fg_stock}</TableCell>
                        <TableCell className="text-right text-yellow-600 font-medium">{combo.remaining}</TableCell>
                        <TableCell className="text-right text-purple-600 font-bold">{combo.possible}</TableCell>
                        <TableCell>
                          {combo.limiting_component ? (
                            <span className="text-red-600 text-sm">{combo.limiting_component}</span>
                          ) : (
                            <span className="text-green-600 text-sm">✓ All available</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(combo.status)}</TableCell>
                      </TableRow>
                      
                      {/* Expanded Component Details */}
                      {expandedCombos.has(combo.combo_barcode) && (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-gray-50 p-4">
                            <div className="text-sm font-medium mb-2">Component Details:</div>
                            <div className="grid gap-2">
                              {combo.components?.map((comp) => (
                                <div key={comp.component_barcode} className="flex items-center justify-between p-2 bg-white rounded border">
                                  <div>
                                    <span className="font-medium">{comp.component_name}</span>
                                    <span className="text-gray-500 text-xs ml-2">({comp.component_barcode})</span>
                                  </div>
                                  <div className="flex items-center gap-4 text-sm">
                                    <span>Qty/Combo: <strong>{comp.qty_per_combo}</strong></span>
                                    <span>Case Pack: <strong>{comp.case_pack_size}</strong></span>
                                    <span>Bundles/Combo: <strong>{comp.bundles_per_combo}</strong></span>
                                    <span className={comp.available_bundles < comp.bundles_per_combo * combo.remaining ? 'text-red-600' : 'text-green-600'}>
                                      Available: <strong>{comp.available_bundles}</strong> bundles
                                    </span>
                                    <span>Possible: <strong>{comp.possible_combos}</strong></span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Plans */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-600" />
            Previous Plans
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingPlans ? (
            <div className="text-center py-4 text-gray-500">Loading plans...</div>
          ) : plans.length === 0 ? (
            <div className="text-center py-4 text-gray-500">No plans found</div>
          ) : (
            <div className="space-y-3">
              {plans.map((plan) => (
                <div key={plan.plan_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <div>
                    <div className="font-medium">{plan.plan_id}</div>
                    <div className="text-sm text-gray-500">
                      Target: {plan.target_month} • Created: {new Date(plan.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(plan.status)}
                    {plan.status === 'APPROVED' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleGeneratePickSlips(plan.plan_id)}
                        disabled={isGenerating}
                        data-testid={`generate-slips-${plan.plan_id}`}
                      >
                        <Boxes className="w-4 h-4 mr-1" />
                        Generate Pick Slips
                      </Button>
                    )}
                    {plan.status === 'PICK_SLIPS_GENERATED' && (
                      <Badge className="bg-green-100 text-green-700">
                        <Check className="w-3 h-3 mr-1" />
                        {plan.sessions_created?.length || 0} Sessions
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review & Approve Plan</DialogTitle>
            <DialogDescription>
              Modify quantities if needed, then approve to reserve stock and generate pick slips.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {currentPlan?.combos?.filter(c => c.remaining > 0).map((combo) => (
              <div key={combo.combo_barcode} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <div className="font-medium">{combo.combo_name}</div>
                  <div className="text-sm text-gray-500">
                    Remaining: {combo.remaining} • Max Possible: {combo.possible}
                  </div>
                  {combo.limiting_component && (
                    <div className="text-xs text-red-500">
                      Limited by: {combo.limiting_component}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Approve Qty:</Label>
                  <Input
                    type="number"
                    className="w-24"
                    min={0}
                    max={combo.possible}
                    value={modifiedAllocations[combo.combo_barcode] || 0}
                    onChange={(e) => handleAllocationChange(combo.combo_barcode, e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="font-medium text-blue-800">
              Total Combos to Approve: {Object.values(modifiedAllocations).reduce((a, b) => a + b, 0)}
            </div>
            <div className="text-sm text-blue-600">
              Stock will be reserved for approved quantities (no expiry)
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleApprovePlan} disabled={isApproving}>
              {isApproving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Approve & Reserve Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SmartComboPlanningTab;
